import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { mediaUrl } from '../utils/media';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  User, Lock, Bell, Shield, Trash2, AlertTriangle, CheckCircle, XCircle,
  LogOut, CreditCard, Truck, Upload, ArrowRight, Crown, Eye, EyeOff,
  Smartphone, Mail, Monitor, Download, RefreshCw, FileText, Globe,
  Moon, Sun, Minus, Keyboard, Volume2
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'account', icon: User, label: 'Account' },
  { id: 'security', icon: Lock, label: 'Security' },
  { id: 'notifications', icon: Bell, label: 'Notifications' },
  { id: 'privacy', icon: Shield, label: 'Privacy' },
  { id: 'appearance', icon: Monitor, label: 'Appearance' },
  { id: 'danger', icon: Trash2, label: 'Danger Zone' },
];

const Toggle = ({ enabled, onChange, testId }) => (
  <button onClick={onChange} className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`} data-testid={testId}>
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
  </button>
);

const SectionCard = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
    {children}
  </motion.div>
);

const SettingRow = ({ label, description, children }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <div className="flex-1 min-w-0 mr-4">
      <p className="text-sm font-medium text-slate-900">{label}</p>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [truckUploading, setTruckUploading] = useState(false);

  // Password change
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Settings from API
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/profile/settings');
      setSettings(res.data);
      // Apply theme on load
      const theme = res.data?.appearance?.theme;
      document.documentElement.classList.remove('dark', 'light');
      if (theme === 'dark') document.documentElement.classList.add('dark');
      else if (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) document.documentElement.classList.add('dark');
    } catch { /* defaults used */ }
    finally { setSettingsLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateSetting = async (category, key, value) => {
    const prev = { ...settings };
    setSettings(s => ({ ...s, [category]: { ...s[category], [key]: value } }));
    try {
      await api.put('/profile/settings', { category, updates: { [key]: value } });
      const labels = {
        email_alerts: 'Email notifications', push_alerts: 'Push notifications', sms_alerts: 'SMS notifications',
        in_app_alerts: 'In-app notifications', marketplace_alerts: 'Marketplace alerts', community_alerts: 'Community alerts',
        message_alerts: 'Message alerts', mentorship_alerts: 'Mentorship alerts', ai_recommendations: 'AI recommendations',
        fraud_alerts: 'Fraud alerts', profile_visible: 'Profile visibility', searchable: 'Search visibility',
        show_email: 'Email display', show_phone: 'Phone display', allow_messages: 'Direct messages',
        data_sharing: 'Data sharing', two_factor_enabled: 'Two-factor auth', login_alerts: 'Login alerts',
        security_alerts: 'Security alerts', compact_mode: 'Compact mode', reduced_motion: 'Reduced motion',
      };
      const label = labels[key] || key.replace(/_/g, ' ');
      toast.success(`${label} ${typeof value === 'boolean' ? (value ? 'enabled' : 'disabled') : 'updated'}`);

      // Apply theme changes immediately
      if (key === 'theme') {
        document.documentElement.classList.remove('dark', 'light');
        if (value === 'dark') document.documentElement.classList.add('dark');
        else if (value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) document.documentElement.classList.add('dark');
      }
    } catch {
      setSettings(prev);
      toast.error('Failed to update setting');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Password changed successfully');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  const handleExportData = async () => {
    try {
      const res = await api.get('/profile/data-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'my_straightconnect_data.json';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch { toast.error('Failed to export data'); }
  };

  const handleDeactivateAccount = async () => {
    if (confirmText !== 'DELETE') { toast.error('Please type DELETE to confirm'); return; }
    setDeactivating(true);
    try {
      await api.post('/auth/deactivate', { reason: deactivateReason });
      toast.success('Account deactivated successfully');
      logout();
    } catch { toast.error('Failed to deactivate account'); }
    finally { setDeactivating(false); }
  };

  const pwStrength = (() => {
    const p = pwForm.newPw;
    if (!p) return { score: 0, label: '', color: '' };
    let s = 0;
    if (p.length >= 6) s++; if (p.length >= 10) s++;
    if (/[A-Z]/.test(p)) s++; if (/[0-9]/.test(p)) s++; if (/[^A-Za-z0-9]/.test(p)) s++;
    const map = [
      { label: 'Weak', color: 'bg-red-500' }, { label: 'Weak', color: 'bg-red-500' },
      { label: 'Fair', color: 'bg-yellow-500' }, { label: 'Good', color: 'bg-blue-500' },
      { label: 'Strong', color: 'bg-green-500' }, { label: 'Very Strong', color: 'bg-green-600' },
    ];
    return { score: s, ...map[s] };
  })();

  const n = settings?.notifications || {};
  const p = settings?.privacy || {};
  const a = settings?.appearance || {};
  const sec = settings?.security || {};

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="settings-title">Account Settings</h1>
          <p className="text-slate-600">Manage your account preferences and security</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm sticky top-4">
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${activeTab === tab.id ? 'text-slate-900 bg-blue-50' : tab.id === 'danger' ? 'text-red-600 hover:bg-red-50' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`tab-${tab.id}`}>
                    <tab.icon className="h-5 w-5" />{tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* ========== ACCOUNT TAB ========== */}
            {activeTab === 'account' && (<>
              <SectionCard>
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Profile Information</h2>
                <div className="space-y-4">
                  <div><label className="text-sm font-medium text-slate-700">Name</label><p className="text-slate-900 mt-1">{user?.name}</p></div>
                  <div><label className="text-sm font-medium text-slate-700">Email</label><p className="text-slate-900 mt-1">{user?.email}</p></div>
                  <div><label className="text-sm font-medium text-slate-700">Account Type</label><p className="text-slate-900 mt-1 capitalize">{user?.role?.replace('_', ' ')}</p></div>
                  <div><label className="text-sm font-medium text-slate-700">Membership</label><p className="text-slate-900 mt-1 capitalize">{user?.membership_tier || 'Free'}</p></div>
                </div>
              </SectionCard>

              <SectionCard delay={0.1}>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Your Profile Type: {{ carrier_owner: 'Carrier / Owner', dispatcher: 'Dispatcher', driver: 'Driver', admin: 'Admin' }[user?.role] || user?.role}</h3>
                    <p className="text-blue-800 text-sm mb-3">
                      {user?.role === 'driver' && 'Access to job boards, credential verification, load matching, and driver resources.'}
                      {user?.role === 'carrier_owner' && 'Access to fleet management, driver hiring, load posting, and carrier compliance tools.'}
                      {user?.role === 'dispatcher' && 'Access to carrier network, load coordination, and dispatch management tools.'}
                    </p>
                    <button onClick={() => navigate('/profile')} className="text-sm font-medium text-blue-600 hover:text-blue-700">Manage Your Profile &rarr;</button>
                  </div>
                </div>
              </SectionCard>

              {user?.role !== 'admin' && (
                <SectionCard delay={0.15}>
                  <div className="flex items-center gap-2 mb-4"><CreditCard className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Manage Subscription</h2></div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <p className="text-slate-900 font-medium">Current Plan: <span className="capitalize font-bold">{user?.membership_tier || 'Free'}</span></p>
                      <p className="text-sm text-slate-500 mt-0.5">View plans, upgrade, or manage your subscription</p>
                    </div>
                    <Link to="/subscription" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors" data-testid="manage-subscription-link">
                      <Crown className="h-4 w-4" />View Plans <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </SectionCard>
              )}

              <SectionCard delay={0.12}>
                <div className="flex items-center gap-2 mb-4"><User className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Profile Picture</h2></div>
                <p className="text-sm text-slate-500 mb-4">Upload a professional photo. JPG or PNG, min 400x400px, max 5MB.</p>
                <div className="flex items-center gap-4">
                  {user?.profile_picture ? <img src={mediaUrl(user.profile_picture)} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" />
                    : <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-200"><User className="h-8 w-8 text-slate-400" /></div>}
                  <label className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-700 transition-colors">
                    <Upload className="h-4 w-4" />Upload Profile Photo
                    <input type="file" accept="image/jpeg,image/jpg,image/png" className="hidden" data-testid="settings-profile-picture-input"
                      onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; try { const fd = new FormData(); fd.append('file', file); await api.post('/profile/picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Profile picture updated!'); window.location.reload(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); } }} />
                  </label>
                </div>
              </SectionCard>

              {user?.role === 'driver' && (
                <SectionCard delay={0.18}>
                  <div className="flex items-center gap-2 mb-4"><Truck className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Truck Picture</h2></div>
                  <p className="text-sm text-slate-500 mb-4">Upload a photo of your truck to display on your profile.</p>
                  <div className="flex items-center gap-4">
                    {user?.truck_picture && <img src={mediaUrl(user.truck_picture)} alt="Truck" className="w-20 h-20 rounded-lg object-cover border border-slate-200" />}
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-slate-800 transition-colors">
                      <Upload className="h-4 w-4" />{truckUploading ? 'Uploading...' : 'Upload Truck Photo'}
                      <input type="file" accept="image/*" className="hidden" disabled={truckUploading} data-testid="truck-upload-input"
                        onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; setTruckUploading(true); try { const fd = new FormData(); fd.append('file', file); await api.post('/users/truck-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Truck picture uploaded!'); window.location.reload(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); } finally { setTruckUploading(false); } }} />
                    </label>
                  </div>
                </SectionCard>
              )}
            </>)}

            {/* ========== SECURITY TAB ========== */}
            {activeTab === 'security' && (<>
              <SectionCard>
                <div className="flex items-center gap-2 mb-4"><Lock className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Change Password</h2></div>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                    <div className="relative">
                      <input type={showPw ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm({ ...pwForm, current: e.target.value })} required
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none pr-10" data-testid="current-password-input" />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                    <input type={showPw ? 'text' : 'password'} value={pwForm.newPw} onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })} required
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="new-password-input" />
                    {pwForm.newPw && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${pwStrength.color}`} style={{ width: `${(pwStrength.score / 5) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium text-slate-500">{pwStrength.label}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                    <input type={showPw ? 'text' : 'password'} value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} required
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="confirm-new-password-input" />
                    {pwForm.confirm && pwForm.newPw !== pwForm.confirm && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
                  </div>
                  <button type="submit" disabled={pwLoading || !pwForm.current || !pwForm.newPw || pwForm.newPw !== pwForm.confirm}
                    className="bg-blue-600 text-white px-6 py-2.5 rounded-md font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center gap-2" data-testid="change-password-btn">
                    {pwLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}Change Password
                  </button>
                </form>
              </SectionCard>

              <SectionCard delay={0.1}>
                <div className="flex items-center gap-2 mb-4"><Smartphone className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Two-Factor Authentication</h2></div>
                <p className="text-sm text-slate-500 mb-4">Add an extra layer of security to your account</p>
                <SettingRow label="Enable Two-Factor Authentication" description="Require a verification code when signing in">
                  <Toggle enabled={sec.two_factor_enabled} onChange={() => updateSetting('security', 'two_factor_enabled', !sec.two_factor_enabled)} testId="toggle-2fa" />
                </SettingRow>
                {sec.two_factor_enabled && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Verification Method</label>
                    <div className="flex gap-3">
                      {[{ id: 'email', icon: Mail, label: 'Email' }, { id: 'sms', icon: Smartphone, label: 'SMS' }].map(m => (
                        <button key={m.id} onClick={() => updateSetting('security', 'two_factor_method', m.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${sec.two_factor_method === m.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}>
                          <m.icon className="h-4 w-4" />{m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </SectionCard>

              <SectionCard delay={0.15}>
                <div className="flex items-center gap-2 mb-4"><Shield className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Security Alerts</h2></div>
                <SettingRow label="Login Alerts" description="Get notified when someone signs into your account">
                  <Toggle enabled={sec.login_alerts} onChange={() => updateSetting('security', 'login_alerts', !sec.login_alerts)} testId="toggle-login-alerts" />
                </SettingRow>
                <SettingRow label="Security Alerts" description="Get notified about security-related account changes">
                  <Toggle enabled={sec.security_alerts} onChange={() => updateSetting('security', 'security_alerts', !sec.security_alerts)} testId="toggle-security-alerts" />
                </SettingRow>
              </SectionCard>

              <SectionCard delay={0.2}>
                <div className="flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Verification & Compliance</h2></div>
                <p className="text-sm text-slate-500 mb-3">Manage your identity verification and compliance documents</p>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="text-slate-900 font-medium">Verification Status: <span className={`capitalize font-bold ${user?.verification_status === 'approved' ? 'text-green-600' : 'text-yellow-600'}`}>{user?.verification_status || 'Pending'}</span></p>
                    <p className="text-sm text-slate-500 mt-0.5">Upload and manage your DOT, TWIC, insurance, CDL, W9, and EIN documents</p>
                  </div>
                  <Link to="/profile" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors">
                    <FileText className="h-4 w-4" />Manage Documents
                  </Link>
                </div>
              </SectionCard>
            </>)}

            {/* ========== NOTIFICATIONS TAB ========== */}
            {activeTab === 'notifications' && (<>
              <SectionCard>
                <div className="flex items-center gap-2 mb-4"><Bell className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Notification Channels</h2></div>
                <SettingRow label="Email Notifications" description="Receive important updates via email">
                  <Toggle enabled={n.email_alerts} onChange={() => updateSetting('notifications', 'email_alerts', !n.email_alerts)} testId="toggle-email" />
                </SettingRow>
                <SettingRow label="Push Notifications" description="Browser push notifications">
                  <Toggle enabled={n.push_alerts} onChange={() => updateSetting('notifications', 'push_alerts', !n.push_alerts)} testId="toggle-push" />
                </SettingRow>
                <SettingRow label="SMS Notifications" description="Text message alerts for critical updates">
                  <Toggle enabled={n.sms_alerts} onChange={() => updateSetting('notifications', 'sms_alerts', !n.sms_alerts)} testId="toggle-sms" />
                </SettingRow>
                <SettingRow label="In-App Notifications" description="Notifications within the platform">
                  <Toggle enabled={n.in_app_alerts} onChange={() => updateSetting('notifications', 'in_app_alerts', !n.in_app_alerts)} testId="toggle-inapp" />
                </SettingRow>
              </SectionCard>

              <SectionCard delay={0.1}>
                <div className="flex items-center gap-2 mb-4"><Truck className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Activity Notifications</h2></div>
                <SettingRow label="Marketplace & Load Alerts" description="New loads, job postings, and opportunities">
                  <Toggle enabled={n.marketplace_alerts} onChange={() => updateSetting('notifications', 'marketplace_alerts', !n.marketplace_alerts)} testId="toggle-marketplace" />
                </SettingRow>
                <SettingRow label="Community & Messaging" description="New messages, comments, and community activity">
                  <Toggle enabled={n.community_alerts} onChange={() => updateSetting('notifications', 'community_alerts', !n.community_alerts)} testId="toggle-community" />
                </SettingRow>
                <SettingRow label="Message Alerts" description="Direct messages from other members">
                  <Toggle enabled={n.message_alerts} onChange={() => updateSetting('notifications', 'message_alerts', !n.message_alerts)} testId="toggle-messages" />
                </SettingRow>
                <SettingRow label="Mentorship & Activity" description="Mentorship updates and platform activity">
                  <Toggle enabled={n.mentorship_alerts} onChange={() => updateSetting('notifications', 'mentorship_alerts', !n.mentorship_alerts)} testId="toggle-mentorship" />
                </SettingRow>
              </SectionCard>

              <SectionCard delay={0.15}>
                <div className="flex items-center gap-2 mb-4"><Shield className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Smart Notifications</h2></div>
                <SettingRow label="AI Recommendations" description="Personalized job and resource recommendations">
                  <Toggle enabled={n.ai_recommendations} onChange={() => updateSetting('notifications', 'ai_recommendations', !n.ai_recommendations)} testId="toggle-ai" />
                </SettingRow>
                <SettingRow label="Fraud & Safety Alerts" description="Suspicious activity and fraud prevention">
                  <Toggle enabled={n.fraud_alerts} onChange={() => updateSetting('notifications', 'fraud_alerts', !n.fraud_alerts)} testId="toggle-fraud" />
                </SettingRow>
              </SectionCard>
            </>)}

            {/* ========== PRIVACY TAB ========== */}
            {activeTab === 'privacy' && (<>
              <SectionCard>
                <div className="flex items-center gap-2 mb-4"><Eye className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Profile Visibility</h2></div>
                <SettingRow label="Public Profile" description="Make your profile visible to other members">
                  <Toggle enabled={p.profile_visible} onChange={() => updateSetting('privacy', 'profile_visible', !p.profile_visible)} testId="toggle-profile-visible" />
                </SettingRow>
                <SettingRow label="Searchable" description="Allow your profile to appear in search results">
                  <Toggle enabled={p.searchable} onChange={() => updateSetting('privacy', 'searchable', !p.searchable)} testId="toggle-searchable" />
                </SettingRow>
              </SectionCard>

              <SectionCard delay={0.1}>
                <div className="flex items-center gap-2 mb-4"><Globe className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Communication Permissions</h2></div>
                <SettingRow label="Show Email Address" description="Display your email on your public profile">
                  <Toggle enabled={p.show_email} onChange={() => updateSetting('privacy', 'show_email', !p.show_email)} testId="toggle-show-email" />
                </SettingRow>
                <SettingRow label="Show Phone Number" description="Display your phone number on your public profile">
                  <Toggle enabled={p.show_phone} onChange={() => updateSetting('privacy', 'show_phone', !p.show_phone)} testId="toggle-show-phone" />
                </SettingRow>
                <SettingRow label="Allow Messages" description="Let other members send you direct messages">
                  <Toggle enabled={p.allow_messages} onChange={() => updateSetting('privacy', 'allow_messages', !p.allow_messages)} testId="toggle-allow-messages" />
                </SettingRow>
                <SettingRow label="Data Sharing" description="Allow anonymized data to improve platform recommendations">
                  <Toggle enabled={p.data_sharing} onChange={() => updateSetting('privacy', 'data_sharing', !p.data_sharing)} testId="toggle-data-sharing" />
                </SettingRow>
              </SectionCard>

              <SectionCard delay={0.15}>
                <div className="flex items-center gap-2 mb-4"><Download className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Your Data</h2></div>
                <p className="text-sm text-slate-500 mb-4">Download a copy of all your StraightConnect data including profile, badges, and settings.</p>
                <button onClick={handleExportData}
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-testid="export-data-btn">
                  <Download className="h-4 w-4" />Download My Data
                </button>
              </SectionCard>
            </>)}

            {/* ========== APPEARANCE TAB ========== */}
            {activeTab === 'appearance' && (<>
              <SectionCard>
                <div className="flex items-center gap-2 mb-4"><Monitor className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Theme</h2></div>
                <div className="flex gap-3">
                  {[{ id: 'light', icon: Sun, label: 'Light' }, { id: 'dark', icon: Moon, label: 'Dark' }, { id: 'system', icon: Monitor, label: 'System' }].map(t => (
                    <button key={t.id} onClick={() => updateSetting('appearance', 'theme', t.id)}
                      className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium border transition-colors ${a.theme === t.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}
                      data-testid={`theme-${t.id}`}>
                      <t.icon className="h-4 w-4" />{t.label}
                    </button>
                  ))}
                </div>
              </SectionCard>

              <SectionCard delay={0.1}>
                <div className="flex items-center gap-2 mb-4"><Minus className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Display</h2></div>
                <SettingRow label="Font Size" description="Adjust the text size across the platform">
                  <select value={a.font_size} onChange={e => updateSetting('appearance', 'font_size', e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="font-size-select">
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </SettingRow>
                <SettingRow label="Compact Mode" description="Reduce spacing for a denser layout">
                  <Toggle enabled={a.compact_mode} onChange={() => updateSetting('appearance', 'compact_mode', !a.compact_mode)} testId="toggle-compact" />
                </SettingRow>
              </SectionCard>

              <SectionCard delay={0.15}>
                <div className="flex items-center gap-2 mb-4"><Keyboard className="h-5 w-5 text-blue-600" /><h2 className="font-heading text-xl font-bold text-slate-900">Accessibility</h2></div>
                <SettingRow label="Reduced Motion" description="Minimize animations and transitions">
                  <Toggle enabled={a.reduced_motion} onChange={() => updateSetting('appearance', 'reduced_motion', !a.reduced_motion)} testId="toggle-reduced-motion" />
                </SettingRow>
              </SectionCard>
            </>)}

            {/* ========== DANGER ZONE TAB ========== */}
            {activeTab === 'danger' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-red-200 rounded-md p-6 shadow-sm">
                <h2 className="font-heading text-xl font-bold text-red-900 mb-2">Danger Zone</h2>
                <p className="text-slate-600 text-sm mb-4">Once you deactivate your account, there is no going back. Please be certain.</p>
                <button onClick={() => setShowDeactivateModal(true)}
                  className="bg-red-500 text-white px-6 py-3 rounded-md font-semibold hover:bg-red-600 transition-all flex items-center gap-2" data-testid="deactivate-account-btn">
                  <Trash2 className="h-5 w-5" />Deactivate Account
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </main>

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-md max-w-md w-full p-6 shadow-xl" data-testid="deactivate-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-2 rounded-full"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
              <h3 className="font-heading text-xl font-bold text-slate-900">Deactivate Account</h3>
            </div>
            <p className="text-slate-700 mb-4">This action will permanently deactivate your account and remove all your data. This cannot be undone.</p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Reason for leaving (optional)</label>
                <textarea value={deactivateReason} onChange={e => setDeactivateReason(e.target.value)} rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent" placeholder="Help us improve..." data-testid="deactivate-reason" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Type <span className="font-bold text-red-600">DELETE</span> to confirm</label>
                <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent" placeholder="DELETE" data-testid="confirm-delete-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowDeactivateModal(false); setConfirmText(''); setDeactivateReason(''); }}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-md font-semibold text-slate-700 hover:bg-slate-50 transition-all" data-testid="cancel-deactivate-btn">Cancel</button>
              <button onClick={handleDeactivateAccount} disabled={deactivating || confirmText !== 'DELETE'}
                className="flex-1 bg-red-500 text-white px-4 py-3 rounded-md font-semibold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="confirm-deactivate-btn">
                {deactivating ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Deactivating...</> : <><Trash2 className="h-4 w-4" />Deactivate</>}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Settings;
